import { createSlice, PayloadAction } from "@reduxjs/toolkit"

import type {
  OrgRole,
  OrgUser,
} from "../../../../modules/Settings/UsersManagement/usersManagement.types"

interface OrgUsersCache {
  users: OrgUser[]
  orgRoles: OrgRole[]
  availableRoles: OrgRole[]
  // Keyed by okta_user_id (or the user's id as a fallback).
  rolesMap: Record<string, OrgRole[]>
  lastUpdatedAt: string
}

interface UsersManagementState {
  // Keyed by orgId so admins switching org context never mix up lists.
  byOrgId: Record<string, OrgUsersCache>
}

const initialState: UsersManagementState = {
  byOrgId: {},
}

const usersManagementSlice = createSlice({
  name: "usersManagementService",
  initialState,
  reducers: {
    setOrgUsersData: (
      state,
      action: PayloadAction<{
        orgId: string
        users: OrgUser[]
        orgRoles: OrgRole[]
        availableRoles: OrgRole[]
      }>,
    ) => {
      const { orgId, users, orgRoles, availableRoles } = action.payload
      state.byOrgId[orgId] = {
        users,
        orgRoles,
        availableRoles,
        // A fresh user list invalidates any previously loaded roles.
        rolesMap: {},
        lastUpdatedAt: new Date().toISOString(),
      }
    },
    upsertOrgUser: (
      state,
      action: PayloadAction<{ orgId: string; user: OrgUser }>,
    ) => {
      const cache = state.byOrgId[action.payload.orgId]
      if (!cache) return
      const idx = cache.users.findIndex((u) => u.id === action.payload.user.id)
      if (idx === -1) cache.users.push(action.payload.user)
      else cache.users[idx] = action.payload.user
    },
    removeOrgUser: (
      state,
      action: PayloadAction<{ orgId: string; userId: number; rolesKey?: string }>,
    ) => {
      const cache = state.byOrgId[action.payload.orgId]
      if (!cache) return
      cache.users = cache.users.filter((u) => u.id !== action.payload.userId)
      if (action.payload.rolesKey) delete cache.rolesMap[action.payload.rolesKey]
    },
    setUserRoles: (
      state,
      action: PayloadAction<{
        orgId: string
        key: string
        roles: OrgRole[]
        replacesKey?: string
      }>,
    ) => {
      const cache = state.byOrgId[action.payload.orgId]
      if (!cache) return
      cache.rolesMap[action.payload.key] = action.payload.roles
      // Migrate away a stale key when a user gains an okta_user_id after invitation.
      if (
        action.payload.replacesKey &&
        action.payload.replacesKey !== action.payload.key
      ) {
        delete cache.rolesMap[action.payload.replacesKey]
      }
    },
    // Replaces the whole map at once. Prefer setUserRoles for a single key -
    // this one isn't race-safe against concurrent dispatches.
    replaceOrgRolesMap: (
      state,
      action: PayloadAction<{ orgId: string; rolesMap: Record<string, OrgRole[]> }>,
    ) => {
      const cache = state.byOrgId[action.payload.orgId]
      if (!cache) return
      cache.rolesMap = action.payload.rolesMap
    },
  },
})

export const {
  setOrgUsersData,
  upsertOrgUser,
  removeOrgUser,
  setUserRoles,
  replaceOrgRolesMap,
} = usersManagementSlice.actions

export default usersManagementSlice.reducer
